import { Injectable } from '@angular/core';
import {CookieOptions, CookieService} from 'ngx-cookie';
import {Observable, BehaviorSubject} from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import { NS_Enums } from './shared/namespaces';
import { Token } from './app.model';
import { getUserIdFromToken } from './shared/helpers/jwt.helper';
import { isLocal } from './shared/helpers/local.helper';
  @Injectable({
  providedIn: 'root'
})
export class CacheService {
  sharedCookieState$: BehaviorSubject<any> = new BehaviorSubject<any>('INIT');
  private sharedCookieInterval: any = null;

  private _userData$ = new BehaviorSubject<any>(undefined);
  public readonly userData$: Observable<any> = this._userData$.asObservable()
  .pipe(skipWhile(data => data === undefined || data === null));

  private data: { [key: string]: any } = {};

  private static cookieEndDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  }

  private static expiryTime(minutes: number): Date {
    const date = new Date();
    return new Date(date.getTime() + minutes * 60000)
  }

  constructor(private cookieService: CookieService) { }

  /**
   * Cookie name for `key`. Locally every app shares `localhost` cookies, and Pulse uses the
   * same `saras_auth_key` name, so Daton namespaces its own to stop the two clobbering each
   * other. Unchanged outside local dev.
   */
  private cookieKey(key: string): string {
    return isLocal && key === NS_Enums.cacheKeysEnum.saras_auth_key ? `daton_${key}` : key;
  }

  get (key: string): string {
    return this.data[key] ? this.data[key] : this.cookieService.get(this.cookieKey(key));
  }

  getObject<T>(key: string): T {
    const cacheObj = this.data[key] ? this.data[key] as T : this.cookieService.getObject(this.cookieKey(key)) as unknown as T;
    if (key === 'saras_auth_key' && cacheObj) {
      this._userData$.next({ err: null, user: cacheObj });
    } else if (key === 'saras_auth_key' && !cacheObj) {
      this._userData$.next({ err: true, user: null });
    }
    return cacheObj;
  }

  put(key: string, value: string, options?: CookieOptions) {
    this.data[key] = value;
    if (options && options.expires) {
      const expiryDate = CacheService.expiryTime(Number(options.expires))
      this.cookieService.put(this.cookieKey(key), value, {expires: expiryDate});
    }
  }

  putObject<T>(key: string, value: T, cookie?: boolean) {
    this.data[key] = value;
    if (cookie) {
      // if(key === NS_Enums.cacheKeysEnum.saras_auth_key){
      //   this.cookieService.putObject(key, value as unknown as object, {expires: CacheService.cookieEndDate(), domain: '.sarasanalytics.com', path: '/', sameSite: "none"});
      // }
      this.cookieService.putObject(this.cookieKey(key), value as unknown as object, {expires: CacheService.cookieEndDate(), sameSite: 'lax'});
      if (key === NS_Enums.cacheKeysEnum.saras_auth_key) {
        this._userData$.next({ err: null, user: value });
      }
    }
  }

  remove(key: string) {
    delete this.data[key];
    this.cookieService.remove(this.cookieKey(key));
  }

  clear() {
    this.data = {};
    if (isLocal) {
      // Local dev only: all apps share `localhost` cookies, so removeAll() would also wipe the
      // shared global-login cookie and sign every other open app out. Keep it.
      const all = this.cookieService.getAll() || {};
      Object.keys(all)
        .filter((cookieKey) => cookieKey !== NS_Enums.saras_global)
        .forEach((cookieKey) => this.cookieService.remove(cookieKey));
      return;
    }
    this.cookieService.removeAll();
  }

  /**
   * Whether the cached `saras_auth_key` represents a logged-in session.
   * Prefer this over truthiness checks on the raw cached object so empty or
   * partial entries are handled consistently across components.
   * @returns True when the auth key exists and includes a `token` or `userId`
   * @param authKey - Optional pre-fetched auth key to avoid a second cache read
   */
  hasValidAuthKey(authKey?: Token | null): boolean {
    const key = authKey ?? this.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key);
    return !!(key?.token || key?.userId);
  }

  isCurrentUserSameAsGlobalLogin(){
    const saras_global: any = this.getObject(NS_Enums.saras_global);
    const saras_auth_key: any = this.getObject(NS_Enums.cacheKeysEnum.saras_auth_key);
    const authUserId = saras_auth_key?.userId || getUserIdFromToken(saras_auth_key?.token);
    return isLocal ? true : saras_auth_key && saras_global && saras_global?.userId && (String(saras_global?.userId) === String(authUserId));
  }

  /* Shared cookies for Global Login */
  addSharedCookie(token: Token){
    try{
      const tokenStr = JSON.stringify(token);
      document.cookie = `${NS_Enums.saras_global}=${tokenStr}; domain=.sarasanalytics.com; path=/; Secure; SameSite=None`;
    }
    catch(e){
      console.log(e);
    }
  }
  removeSharedCookie(){
    document.cookie = `${NS_Enums.saras_global}=; domain=.sarasanalytics.com; path=/; Secure; SameSite=None`;
  }

  initSharedCookieStatus(){
    try{
      let initial_saras_global = this.cookieService.get(NS_Enums.saras_global as string) || null;
      this.sharedCookieInterval = setInterval(()=>{
        let saras_global = this.cookieService.get(NS_Enums.saras_global as string) || null;
        if(initial_saras_global != saras_global){
          this.sharedCookieState$.next(saras_global);
          initial_saras_global = saras_global;
          // this.removeSharedCookieInterval();
        }
      }, 3000);
    }catch(e){
      console.log(e);
    }
  }

  removeSharedCookieInterval(){
    clearInterval(this.sharedCookieInterval);
    this.sharedCookieInterval = null;
  }

  storeObject(key, value){
    const valueString = JSON.stringify(value);
    localStorage.setItem(key, valueString);
  }
  getStoreObject(key){
    try{
      const valueString = localStorage.getItem(key);
      const value = JSON.parse(valueString);
      return value;
    }catch(e){
      console.log(e);
      return null;
    }
  }
}
